/**
 * THE AUTHORED GUIDANCE A TENANT READS ON A CONTROL — one definition for both surfaces that show it: the Control
 * Library's detail window and the workspace control page (Overview → Control Details).
 *
 * ⚠ ONE LIST, so the two surfaces cannot disagree about which fields exist, what they are called or the order
 *   they read in. Labels and order are Design's, as the admin portal's content panel states them.
 * ⚠ RUNTIME VALUES LIVE HERE, not in an api hooks module: test files replace those modules wholesale with factory
 *   mocks, and a constant exported from one would vanish from every page that imports it under test.
 */

export const CONTROL_GUIDANCE_FIELDS = [
  { key: 'implementation_guidance', label: 'Implementation guidance' },
  { key: 'applicability_guidance', label: 'Applicability guidance' },
  { key: 'expected_evidence', label: 'Expected evidence' },
] as const;

export type ControlGuidanceKey = (typeof CONTROL_GUIDANCE_FIELDS)[number]['key'];

/** The three fields as both payloads carry them: required, and NULL when not authored. */
export type ControlGuidance = Record<ControlGuidanceKey, string | null>;

/**
 * Authored text worth showing. ⚠ NULL, empty and whitespace-only all mean "not authored", and an unauthored field
 * is HIDDEN — no label, no placeholder — never rendered as a blank heading.
 */
export function hasText(v: string | null | undefined): v is string {
  return typeof v === 'string' && v.trim() !== '';
}

const LINE_BREAK = /\r?\n/;
/** A list marker the author typed at the start of a line; the rendered bullet or row replaces it. */
const LIST_MARKER = /^[-*•]\s+/;

/**
 * The items an author wrote on SEPARATE LINES — the only list structure a surface may impose on guidance.
 *
 * ⚠ NEVER SPLITS A SENTENCE OR A SEMICOLON. Guidance is authored as prose in the admin portal; splitting it on
 *   punctuation would rewrite what the author wrote ("e.g.", "v1.2", a clause after a semicolon). Lines are
 *   trimmed, blank lines dropped, and a typed leading marker ("- ", "* ", "• ") removed — nothing else changes.
 *   One line → one item: the surface renders it as a paragraph (or a single evidence row), never a one-bullet list.
 */
export function authoredLines(text: string): string[] {
  return text.split(LINE_BREAK)
    .map((l) => l.trim())
    .filter((l) => l !== '')
    .map((l) => l.replace(LIST_MARKER, ''));
}
